import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Columns3, Check, X, FileDown } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

const fmtAnswer = (q, v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (q.type === "yes_no") return v === true || v === "true" || v === "yes" ? "Yes" : v === false || v === "false" || v === "no" ? "No" : String(v);
  return String(v);
};

export const ApplicantCompare = ({ group, title, onDecide, baseRent = 0, baseDeposit = 0 }) => {
  const [open, setOpen] = useState(false);
  const [perPetRent, setPerPetRent] = useState(40);
  const [perPetDeposit, setPerPetDeposit] = useState(150);
  const [pets, setPets] = useState({});  // { appId: count }

  // Union of custom questions across applicants, keyed by label (template snapshots may differ).
  const labelMap = {};
  group.forEach((a) => (a.template_questions || []).forEach((q) => { if (!labelMap[q.label]) labelMap[q.label] = q; }));
  const questions = Object.values(labelMap);

  const answerFor = (a, q) => {
    const own = (a.template_questions || []).find((x) => x.label === q.label);
    return own ? (a.answers || {})[own.id] : undefined;
  };

  const maxIncome = Math.max(...group.map((a) => Number(a.annual_income || 0)), 0);
  const petCount = (a) => Math.max(0, parseInt(pets[a.id] || 0, 10) || 0);
  const monthlyRent = (a) => Number(baseRent) + petCount(a) * (parseFloat(perPetRent) || 0);
  const moveInDeposit = (a) => Number(baseDeposit) + petCount(a) * (parseFloat(perPetDeposit) || 0);
  const ratio = (a) => {
    const monthlyIncome = Number(a.annual_income || 0) / 12;
    return monthlyIncome > 0 ? monthlyRent(a) / monthlyIncome : null;
  };
  const ratioColor = (r) => (r === null ? "text-mute" : r <= 0.3 ? "text-emerald-600" : r <= 0.4 ? "text-amber-600" : "text-red-600");
  const maxRent = Math.max(...group.map((a) => monthlyRent(a)), 0);

  // At-a-glance match score (0–100): rent-to-income (45) + income vs group (20)
  // + answer completeness (15) + risk-flag signals (20). Higher = stronger applicant.
  const NEG_FLAG = /evict|bankrupt|felon|crimin|smoke|smok/i;
  const scoreOf = (a) => {
    const r = ratio(a);
    let rentScore = 0;
    if (r !== null) rentScore = r <= 0.3 ? 45 : r >= 0.6 ? 0 : Math.round((45 * (0.6 - r)) / 0.3);
    const incScore = maxIncome > 0 ? Math.round((Number(a.annual_income || 0) / maxIncome) * 20) : 0;
    const qs = a.template_questions || [];
    const answered = qs.filter((q) => { const v = (a.answers || {})[q.id]; return v !== undefined && v !== null && v !== ""; }).length;
    const compScore = qs.length ? Math.round((answered / qs.length) * 15) : 15;
    let signal = 20;
    qs.forEach((q) => {
      if (q.type === "yes_no" && NEG_FLAG.test(q.label)) {
        const v = (a.answers || {})[q.id];
        if (v === true || v === "true" || v === "yes") signal = Math.max(0, signal - 10);
      }
    });
    return Math.min(100, Math.max(0, rentScore + incScore + compScore + signal));
  };
  const scores = {};
  group.forEach((a) => { scores[a.id] = scoreOf(a); });
  const topScore = Math.max(...group.map((a) => scores[a.id]), 0);
  const topId = group.find((a) => scores[a.id] === topScore && topScore > 0)?.id;
  const scoreColor = (s) => (s >= 75 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-red-600");
  const scoreBar = (s) => (s >= 75 ? "bg-emerald-500" : s >= 50 ? "bg-amber-500" : "bg-red-500");

  const decide = async (id, status) => {
    await onDecide(id, status);
    setOpen(false);
  };

  const exportPdf = () => {
    const esc = (s) => String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
    const cols = group.map((a) => esc(a.renter_name));
    const rowsData = [
      ["Email", group.map((a) => esc(a.renter_email))],
      ["Match score", group.map((a) => `${scores[a.id]}/100${a.id === topId ? " (Top match)" : ""}`)],
      ["Employer", group.map((a) => esc(a.employer || "—"))],
      ["Annual income", group.map((a) => money(a.annual_income) + (Number(a.annual_income || 0) === maxIncome && maxIncome > 0 ? " ★" : ""))],
      ["Move-in date", group.map((a) => esc(a.move_in_date || "—"))],
      ...questions.map((q) => [esc(q.label), group.map((a) => esc(fmtAnswer(q, answerFor(a, q))))]),
      ["# Pets", group.map((a) => petCount(a))],
      ["Monthly rent collected", group.map((a) => money(monthlyRent(a)))],
      ["Move-in deposit", group.map((a) => money(moveInDeposit(a)))],
      ["Rent-to-income", group.map((a) => { const r = ratio(a); return r === null ? "—" : `${(r * 100).toFixed(0)}%`; })],
    ];
    if (group.some((a) => a.notes)) rowsData.push(["Notes", group.map((a) => esc(a.notes || "—"))]);
    const html = `<html><head><title>Applicant Comparison — ${esc(title)}</title>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#1a1a1a;padding:36px}
      h1{font-size:20px;margin-bottom:2px}.sub{color:#666;font-size:12px;margin-bottom:20px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #e5e5e5;padding:8px 10px;text-align:left;vertical-align:top}
      th{background:#f4f4f2}td.k{font-weight:600;background:#faf9f7;width:22%}
      .foot{margin-top:18px;color:#888;font-size:11px}</style></head><body>
      <h1>Applicant Comparison</h1><div class="sub">${esc(title)} · ${new Date().toLocaleDateString()}</div>
      <table><thead><tr><th>Field</th>${cols.map((c) => `<th>${c}</th>`).join("")}</tr></thead>
      <tbody>${rowsData.map(([k, vals]) => `<tr><td class="k">${k}</td>${vals.map((v) => `<td>${v}</td>`).join("")}</tr>`).join("")}</tbody></table>
      <p class="foot">Match score weighs rent-to-income, income, answer completeness and risk flags. A healthy rent-to-income is ≤ 30%. Generated by LocalLease.</p>
      </body></html>`;
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    iframe.srcdoc = html;
    iframe.onload = () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { console.error("Print failed", e); }
      setTimeout(() => iframe.remove(), 1000);
    };
    document.body.appendChild(iframe);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`compare-btn-${group[0].property_id}`} className="inline-flex items-center gap-1.5 border border-forest text-forest rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-forest/5">
          <Columns3 className="h-4 w-4" /> Compare {group.length} applicants
        </button>
      </DialogTrigger>
      <DialogContent data-testid="compare-dialog" className="bg-white rounded-2xl max-w-[95vw] sm:max-w-4xl max-h-[88vh] overflow-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="font-heading">Compare applicants — {title}</DialogTitle>
              <DialogDescription>Each applicant gets a match score (rent-to-income, income, answer completeness & risk flags) — the strongest is tagged "Top match". Enter pet counts to project rent & deposits. A healthy rent-to-income is ≤ 30%.</DialogDescription>
            </div>
            <button data-testid="compare-export-pdf" onClick={exportPdf} className="shrink-0 inline-flex items-center gap-1.5 border border-line text-ink rounded-lg px-3 py-1.5 text-sm hover:bg-bone">
              <FileDown className="h-4 w-4" /> Export PDF
            </button>
          </div>
        </DialogHeader>
        <div className="flex flex-wrap gap-4 rounded-lg border border-line bg-bone/40 p-3 mb-2 text-sm" data-testid="compare-pet-settings">
          <label className="flex items-center gap-1.5">Per-pet rent $<input data-testid="per-pet-rent" type="number" value={perPetRent} onChange={(e) => setPerPetRent(e.target.value)} className="w-20 border border-line rounded px-2 py-1" />/mo</label>
          <label className="flex items-center gap-1.5">Per-pet deposit $<input data-testid="per-pet-deposit" type="number" value={perPetDeposit} onChange={(e) => setPerPetDeposit(e.target.value)} className="w-20 border border-line rounded px-2 py-1" /></label>
          <span className="text-mute self-center">Base rent {money(baseRent)}/mo · base deposit {money(baseDeposit)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="compare-table">
            <thead>
              <tr>
                <th className="text-left p-2 text-mute font-medium sticky left-0 bg-white w-40">Field</th>
                {group.map((a) => (
                  <th key={a.id} className="text-left p-2 min-w-[170px]">
                    <p className="font-heading font-medium text-ink flex items-center gap-1.5">{a.renter_name}{a.id === topId ? <span data-testid={`top-match-${a.id}`} className="text-[10px] bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5 font-medium">Top match</span> : null}</p>
                    <p className="text-xs text-mute font-normal truncate">{a.renter_email}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-line bg-forest/[0.03]">
                <td className="p-2 text-mute sticky left-0 bg-white font-medium">Match score</td>
                {group.map((a) => (
                  <td key={a.id} data-testid={`score-${a.id}`} className="p-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-base font-heading font-semibold ${scoreColor(scores[a.id])}`}>{scores[a.id]}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-line overflow-hidden max-w-[80px]">
                        <div className={`h-full ${scoreBar(scores[a.id])}`} style={{ width: `${scores[a.id]}%` }} />
                      </div>
                    </div>
                  </td>
                ))}
              </tr>
              <tr className="border-t border-line">
                <td className="p-2 text-mute sticky left-0 bg-white">Employer</td>
                {group.map((a) => <td key={a.id} className="p-2 text-ink">{a.employer || "—"}</td>)}
              </tr>
              <tr className="border-t border-line">
                <td className="p-2 text-mute sticky left-0 bg-white">Annual income</td>
                {group.map((a) => (
                  <td key={a.id} className={`p-2 font-medium ${Number(a.annual_income || 0) === maxIncome && maxIncome > 0 ? "text-forest" : "text-ink"}`}>
                    {money(a.annual_income)}{Number(a.annual_income || 0) === maxIncome && maxIncome > 0 ? " ★" : ""}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-line">
                <td className="p-2 text-mute sticky left-0 bg-white">Move-in date</td>
                {group.map((a) => <td key={a.id} className="p-2 text-ink">{a.move_in_date || "—"}</td>)}
              </tr>
              {questions.map((q) => (
                <tr key={q.label} className="border-t border-line">
                  <td className="p-2 text-mute sticky left-0 bg-white">{q.label}</td>
                  {group.map((a) => <td key={a.id} className="p-2 text-ink">{fmtAnswer(q, answerFor(a, q))}</td>)}
                </tr>
              ))}
              {group.some((a) => a.notes) && (
                <tr className="border-t border-line">
                  <td className="p-2 text-mute sticky left-0 bg-white align-top">Notes</td>
                  {group.map((a) => <td key={a.id} className="p-2 text-ink italic text-xs">{a.notes || "—"}</td>)}
                </tr>
              )}
              <tr className="border-t border-line bg-bone/30">
                <td className="p-2 text-mute sticky left-0 bg-bone/30"># Pets</td>
                {group.map((a) => (
                  <td key={a.id} className="p-2">
                    <input data-testid={`pets-${a.id}`} type="number" min="0" value={pets[a.id] || 0} onChange={(e) => setPets({ ...pets, [a.id]: e.target.value })} className="w-16 border border-line rounded px-2 py-1" />
                  </td>
                ))}
              </tr>
              <tr className="border-t border-line bg-bone/30">
                <td className="p-2 text-mute sticky left-0 bg-bone/30 font-medium">Monthly rent collected</td>
                {group.map((a) => (
                  <td key={a.id} data-testid={`rent-collected-${a.id}`} className={`p-2 font-semibold ${monthlyRent(a) === maxRent && maxRent > 0 ? "text-forest" : "text-ink"}`}>
                    {money(monthlyRent(a))}{petCount(a) > 0 ? <span className="text-xs text-mute font-normal"> (incl. {money(petCount(a) * (parseFloat(perPetRent) || 0))} pet)</span> : ""}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-line bg-bone/30">
                <td className="p-2 text-mute sticky left-0 bg-bone/30 font-medium">Move-in deposit</td>
                {group.map((a) => (
                  <td key={a.id} data-testid={`deposit-collected-${a.id}`} className="p-2 text-ink">
                    {money(moveInDeposit(a))}{petCount(a) > 0 ? <span className="text-xs text-mute"> (incl. {money(petCount(a) * (parseFloat(perPetDeposit) || 0))} pet)</span> : ""}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-line bg-bone/30">
                <td className="p-2 text-mute sticky left-0 bg-bone/30 font-medium">Rent-to-income</td>
                {group.map((a) => {
                  const r = ratio(a);
                  return <td key={a.id} data-testid={`ratio-${a.id}`} className={`p-2 font-semibold ${ratioColor(r)}`}>{r === null ? "—" : `${(r * 100).toFixed(0)}%`}{r !== null && r <= 0.3 ? " ✓" : ""}</td>;
                })}
              </tr>
              <tr className="border-t border-line">
                <td className="p-2 sticky left-0 bg-white"></td>
                {group.map((a) => (
                  <td key={a.id} className="p-2">
                    <div className="flex gap-1.5">
                      <button data-testid={`compare-accept-${a.id}`} onClick={() => decide(a.id, "approved")} className="flex-1 bg-forest text-white rounded-lg py-1.5 text-xs font-medium hover:bg-forest-hover flex items-center justify-center gap-1"><Check className="h-3.5 w-3.5" /> Accept</button>
                      <button data-testid={`compare-decline-${a.id}`} onClick={() => decide(a.id, "rejected")} className="flex-1 border border-red-200 text-red-600 rounded-lg py-1.5 text-xs font-medium hover:bg-red-50 flex items-center justify-center gap-1"><X className="h-3.5 w-3.5" /> Decline</button>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
